/* Route prefetch registry — exposes a `prefetch(path)` helper that
   triggers the corresponding dynamic import for any route. We wire
   this to onMouseEnter / onFocus / onTouchStart on every Navbar link
   so the chunk is already cached by the time the user clicks.

   When a user hovers a top-level mega-menu trigger we kick off the
   chunk for the most-likely destination in that section. When the
   user actually hovers a specific Link, we fetch that Link's chunk.

   These imports are the SAME module specifiers used in App.js so
   webpack returns the cached chunk instead of fetching twice. */
const LOADERS = {
  "/challenges":               () => import("@/pages/ChallengesPage"),
  "/faq":                      () => import("@/pages/FAQFullPage"),
  "/partner":                  () => import("@/pages/PartnerFullPage"),
  "/how-it-works":             () => import("@/pages/HowItWorksFullPage"),
  "/calculator":               () => import("@/pages/CalculatorFullPage"),
  "/contact":                  () => import("@/pages/ContactFullPage"),
  "/reviews":                  () => import("@/pages/ReviewsPage"),
  "/platforms/compare":        () => import("@/pages/ComparePage"),
  // All info pages share a single chunk (consolidated into `@/pages/info`),
  // so prefetching ANY info route warms the whole shared chunk.
  "/about":                    () => import("@/pages/info"),
  "/general-rules":            () => import("@/pages/info"),
  "/affiliates":               () => import("@/pages/info"),
  "/affiliates/influencers":   () => import("@/pages/info"),
  "/affiliates/dashboard":     () => import("@/pages/info"),
  "/payout-protector":         () => import("@/pages/info"),
  "/blog":                     () => import("@/pages/info"),
  "/platforms/ctrader":        () => import("@/pages/info"),
  "/platforms/dxtrade":        () => import("@/pages/info"),
  "/platforms/matchtrader":    () => import("@/pages/info"),
  "/platforms/gooeypro":       () => import("@/pages/info"),
  "/platforms/web":            () => import("@/pages/info"),
  "/platforms/mobile":         () => import("@/pages/info"),
  "/legal/terms":              () => import("@/pages/LegalPage"),
  "/legal/privacy":            () => import("@/pages/LegalPage"),
  "/legal/cookies":            () => import("@/pages/LegalPage"),
  "/legal/refund":             () => import("@/pages/LegalPage"),
};

const requested = new Set();

/** Warm the chunk for a given route. Idempotent — only fires once per route. */
export function prefetch(path) {
  if (!path || requested.has(path)) return;
  // Match exact route, then fall back to prefix match (e.g. /legal/* )
  const loader =
    LOADERS[path] ||
    Object.entries(LOADERS).find(([key]) => path.startsWith(key + "/"))?.[1];
  if (!loader) return;
  requested.add(path);
  // Catch — a failed prefetch must NEVER throw an unhandled rejection.
  loader().catch(() => {
    requested.delete(path); // allow retry on next hover
  });
}

/** Convenience props spread onto any link/button you want to prefetch on. */
export function prefetchProps(path) {
  const handler = () => prefetch(path);
  return {
    onMouseEnter: handler,
    onFocus: handler,
    onTouchStart: handler,
  };
}

/** Warm ALL routes during browser idle time. Call once after the
 *  initial homepage paint. Chunks are queued; the browser fetches
 *  them when bandwidth is idle, so subsequent nav clicks are instant
 *  even for routes the user never hovered. */
export function prefetchAllIdle() {
  const run = () => {
    for (const path of Object.keys(LOADERS)) prefetch(path);
  };
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(run, { timeout: 4000 });
  } else {
    setTimeout(run, 2000);
  }
}
